/**
 * Catalog detail (preview) — loads one catalog via `mApi.getCatalog` and shows
 * its header, the deep-link ↗ to the real Oxbo-M admin (role-gated, §22c), and
 * a READ-ONLY table of its values. No row actions, no editing — Oxbo-Z is a
 * read-only consumer; the ↗ is the only way from here into anything mutable,
 * and that lands in Oxbo-M's own admin (the security boundary, §22c.3).
 */
import { useMemo } from "react";
import { useLocale } from "@hooks/useLocale";
import { useGetCatalogQuery } from "@api/mApi";
import { useCapabilities } from "@components/catalog/useCapabilities";
import { QueryState } from "@components/ui/QueryState";
import { CATALOG_BY_KEY } from "./register";
import type { CatalogValue } from "@api/types";

export interface CatalogDetailProps {
  catalogKey: string;
}

/** Columns a catalog shows depend on its shape (§23a/§23b). */
function attrColumns(values: CatalogValue[]): string[] {
  const keys = new Set<string>();
  for (const v of values) {
    if (v.attrs) for (const k of Object.keys(v.attrs)) keys.add(k);
  }
  return Array.from(keys);
}

function fmtAttr(x: unknown): string {
  if (x == null) return "—";
  if (typeof x === "boolean") return x ? "✓" : "—";
  return String(x);
}

export function CatalogDetail({ catalogKey }: CatalogDetailProps) {
  const { t } = useLocale();
  const { can } = useCapabilities();
  const reg = CATALOG_BY_KEY[catalogKey];

  const { data, isLoading, isError, refetch } = useGetCatalogQuery({ key: catalogKey });

  const values = useMemo(() => (Array.isArray(data?.values) ? data.values : []), [data]);
  const attrCols = useMemo(() => attrColumns(values), [values]);

  // §22c: the ↗ appears only when the payload carried `admin` (server omits it
  // for non-authorised callers) AND the user holds the catalog's capability.
  // For a non-admin, `admin` is absent → showAdmin is false → no URL in the DOM.
  const admin = data?.admin;
  const showAdmin = !!admin && can(admin.capability);

  const label = data?.label ?? reg?.label ?? catalogKey;
  const shape = data?.shape ?? reg?.shape;
  const isTree = shape === "tree";

  return (
    <div className="card catadmin-detail">
      <div className="catadmin-detail-head">
        <div>
          <h2>{label}</h2>
          <div className="catadmin-detail-key">
            <span className="mono">{catalogKey}</span>
            {shape ? <span className="badge">{t("catadmin.shape")} · {shape}</span> : null}
            {data ? <span className="badge">{t("catadmin.valueCount", { n: values.length })}</span> : null}
            {data?.version ? (
              <span className="badge">
                {t("catadmin.version")} · <span className="mono">{data.version}</span>
              </span>
            ) : null}
          </div>
          {reg?.seed ? (
            <p className="catadmin-seed">
              {t("catadmin.seededFrom")} <span className="mono">{reg.seed}</span>
            </p>
          ) : null}
        </div>

        {/* Deep-link ↗ to the REAL Oxbo-M admin — the only route to CRUD, and it
            leaves Oxbo-Z. Role-gated + URL omitted for non-admins (§22c/§22f). */}
        {showAdmin ? (
          <a
            className="catadmin-manage-link"
            href={admin.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("catadmin.manageAria")}
          >
            {t("catadmin.manage")} <span aria-hidden="true">↗</span>
          </a>
        ) : null}
      </div>

      <div className="table-scroll">
        <QueryState
          isLoading={isLoading}
          isError={isError}
          isEmpty={values.length === 0}
          emptyMessage={t("catadmin.noValues")}
          onRetry={refetch}
        >
          <table>
            <thead>
              <tr>
                <th>{t("catadmin.col.code")}</th>
                <th>{t("catadmin.col.label")}</th>
                {isTree ? <th>{t("catadmin.col.parent")}</th> : null}
                {attrCols.map((c) => (
                  <th key={c}>{c}</th>
                ))}
                <th>{t("catadmin.col.aliases")}</th>
                <th>{t("catadmin.col.active")}</th>
              </tr>
            </thead>
            <tbody>
              {values.map((v) => (
                <tr key={v.code} data-owner="m" className={v.active === false ? "catadmin-inactive" : ""}>
                  <td>
                    <span className="mono catadmin-code">{v.code}</span>
                  </td>
                  <td>{v.label}</td>
                  {isTree ? <td className="mono">{v.parent ?? "—"}</td> : null}
                  {attrCols.map((c) => (
                    <td key={c} className="catadmin-attr">
                      {fmtAttr(v.attrs?.[c])}
                    </td>
                  ))}
                  <td className="catadmin-aliases">
                    {Array.isArray(v.aliases) && v.aliases.length > 0 ? v.aliases.join(", ") : "—"}
                  </td>
                  <td>
                    {v.active === false ? (
                      <span className="badge">{t("catadmin.retired")}</span>
                    ) : (
                      <span className="badge status-producing">{t("catadmin.on")}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </QueryState>
      </div>

      {/* Governance note (§22 wireframe) — read-only reminder of why this is a
          preview and not an editor. */}
      <p className="catadmin-govnote">
        <span aria-hidden="true">⛭</span>
        {t("catadmin.govnote")}
      </p>
    </div>
  );
}
